import React, { useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import xrange from "highcharts/modules/xrange";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import dayjs, { Dayjs } from "dayjs";
import {
  useGetAttananceMainDhashboardQuery,
  useGetCheckAttananceStatusQuery,
} from "../../../Api/attoDeskApi";

xrange(Highcharts);

Highcharts.setOptions({
  time: {
    useUTC: false,
  },
});

const lightThemeColor = JSON.parse(
  localStorage.getItem("lightThemeColor") || "{}"
);
const setCardBRadius = lightThemeColor?.cardBorderRadius || "none";

interface AttendanceEntry {
  date: string;
  empid: string;
  employeeName: string;
  schedules: { start: Date; end: Date }[];
  actualPunches?: { start: Date; end: Date }[];
}

const AttendanceTimeline = () => {
  const theme = useTheme();
  const today = dayjs().startOf("day").format("YYYY-MM-DD");

  const startOfDay = dayjs(today).startOf("day").valueOf();
  const endOfDay = dayjs(today).endOf("day").valueOf();

  // const {
  //   data: attanceData,
  //   isLoading: attanceIsLoaing,
  //   isError: attanceIsError,
  //   refetch: attananceRefetch,
  // } = useGetCheckAttananceStatusQuery({ id: 1 }, { pollingInterval: 1000 });

  const {
    data: dashboardData,
    isLoading: dashboardIsLoading,
    refetch,
  } = useGetAttananceMainDhashboardQuery(
    {
      startDate: today,
    },
    { pollingInterval: 60 * 1000 }
  );

  const attendanceData = useMemo(() => {
    return (dashboardData?.data as AttendanceEntry[]) || [];
  }, [dashboardData?.data]);

  // React.useEffect(() => {
  //   if (attanceData?.active_status == "1") {
  //     refetch();
  //   }
  // }, [attanceData, refetch]);

  const categories =
    attendanceData?.map((entry) => entry.employeeName).reverse() || [];

  const scheduleSeries = attendanceData.flatMap((entry, index) =>
    entry.schedules.map((interval) => ({
      x: dayjs(interval.start).valueOf(),
      x2: dayjs(interval.end).valueOf(),
      y: attendanceData.length - 1 - index,
    }))
  );

  const actualSeries = attendanceData.flatMap((entry, index) =>
    (entry.actualPunches || []).map((interval) => ({
      x: dayjs(interval.start).valueOf(),
      // x2: dayjs(interval.end).valueOf(),
      x2: interval.end ? dayjs(interval.end).valueOf() : dayjs().valueOf(),
      y: attendanceData.length - 1 - index,
    }))
  );

  const options: Highcharts.Options = {
    chart: { type: "xrange", backgroundColor: theme.palette.cardAdd.bgColor },
    // title: {
    //   text: "Today's Attendance",
    //   style: {
    //     // color: primaryColor,
    //     fontSize: "0.9375rem",
    //     color: theme.palette.heading.headingColor,
    //   },
    // },
    title: { text: undefined },
    xAxis: {
      type: "datetime",
      min: startOfDay,
      max: endOfDay,
      tickInterval: 1 * 60 * 60 * 1000,
      labels: {
        format: "{value:%b-%d %I:%M %p}",
        rotation: -30,
        align: "right",
        style: {
          fontSize: "0.75rem",
          color: theme.palette.heading.headingColor,
          minWidth: "900px",
        },
      },
    },
    yAxis: {
      categories,
      title: {
        text: "Staff",
        style: {
          fontSize: "0.75rem",
          color: theme.palette.heading.headingColor,
        },
      },
      reversed: true,
      labels: {
        rotation: -30,
        align: "right",
        style: {
          fontSize: "0.75rem",
          color: theme.palette.heading.headingColor,
        },
      },
      // gridLineWidth: 0,
      // plotBands: [],
    },
    legend: {
      itemStyle: {
        fontSize: "0.75rem",
        color: theme.palette.heading.headingColor,
      },
    },
    series: [
      {
        name: "Actual Punch",
        type: "xrange",
        pointWidth: 12,
        data: actualSeries,
        colorByPoint: false,
        color:
          Highcharts.color(theme.palette.primary.main).brighten(0.2).get() ||
          "#008000",
        borderColor:
          Highcharts.color(theme.palette.primary.main).brighten(0.2).get() ||
          "#008000",
        dataLabels: { enabled: false },
      },
      {
        name: "Schedule",
        type: "xrange",
        pointWidth: 12,
        data: scheduleSeries,
        colorByPoint: false,
        color: Highcharts.color(theme.palette.primary.main).get() || "#FFA500",
        borderColor:
          Highcharts.color(theme.palette.primary.main).get() || "#FFA500",
        dataLabels: { enabled: false },
      },
    ],
    tooltip: {
      useHTML: true,
      backgroundColor: theme.palette.cardAdd.bgColor,
      borderColor: theme.palette.primary.main,
      borderRadius: setCardBRadius,
      style: {
        padding: "8px",
        fontSize: "0.8rem",
        color: theme.palette.heading.headingColor,
      },
      pointFormatter: function () {
        const point = this as any;
        return `
          <div style="
            padding: 8px;
            font-size: 0.8rem;
            color: ${theme.palette.heading.headingColor};
            background-color: ${theme.palette.cardAdd.bgColor};
            border-radius: ${setCardBRadius}px;
            line-height: 1.4;
            min-width: 140px;
          ">
            <div style="font-weight: 600; margin-bottom: 4px;">${
              point.series.name
            }</div>
            <ul style="padding-left: 16px; margin: 8px; list-style: disc;">
              <li>Start: <span style="font-weight:500;">${Highcharts.dateFormat(
                "%e %b, %I:%M %p",
                point.x
              )}</span></li>
              <li>End: <span style="font-weight:500;">${Highcharts.dateFormat(
                "%e %b, %I:%M %p",
                point.x2
              )}</span></li>
            </ul>
          </div>
        `;
      },
    },
  };

  return (
    <div style={{ overflowX: "auto" }}>
      <div style={{ minWidth: "650px" }}>
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
    </div>
  );
};

export default AttendanceTimeline;
